<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4" :loading="loading">
          <v-tabs v-model="tab" grow color="indigo">
            <v-tab value="cert">Certificado</v-tab>
            <v-tab value="perms">Permisos</v-tab>
          </v-tabs>

          <v-tabs-window v-model="tab">
            <!-- ── detalle ──────────────────────────────────────────────── -->
            <v-tabs-window-item value="cert">
              <v-container fluid>
                <v-row>
                  <v-col cols="12" lg="8">
                    <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                    <v-text-field
                      v-else
                      :model-value="data.fingerprint"
                      label="Fingerprint"
                      prepend-inner-icon="mdi-fingerprint"
                      readonly
                    />
                  </v-col>
                  <v-col cols="12" sm="6" lg="4">
                    <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                    <v-text-field
                      v-else
                      :model-value="data.serialnumber"
                      label="Número de serie"
                      prepend-inner-icon="mdi-numeric"
                      readonly
                    />
                  </v-col>
                  <v-col cols="12" sm="6" lg="4">
                    <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                    <v-text-field
                      v-else
                      :model-value="formatDate(data.valid_after)"
                      label="Válido desde"
                      prepend-inner-icon="mdi-calendar-start"
                      readonly
                    />
                  </v-col>
                  <v-col cols="12" sm="6" lg="4">
                    <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                    <v-text-field
                      v-else
                      :model-value="formatDate(data.valid_before)"
                      label="Válido hasta"
                      prepend-inner-icon="mdi-calendar-end"
                      readonly
                      :messages="expiryHint"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" lg="4">
                    <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                    <v-text-field
                      v-else
                      :model-value="mode2FALabel(data.twofa_mode)"
                      label="Segundo factor"
                      prepend-inner-icon="mdi-two-factor-authentication"
                      readonly
                    />
                  </v-col>

                  <!-- Sólo un administrador necesita ver los ids internos. -->
                  <template v-if="isAdmin">
                    <v-col cols="12" sm="6" lg="4">
                      <v-text-field
                        :model-value="data.key_id"
                        label="Llave"
                        prepend-inner-icon="mdi-key"
                        readonly
                      />
                    </v-col>
                    <v-col v-if="data.csr_id" cols="12" sm="6" lg="4">
                      <v-text-field
                        :model-value="data.csr_id"
                        label="CSR"
                        prepend-inner-icon="mdi-file-certificate"
                        readonly
                      />
                    </v-col>
                  </template>

                  <v-col cols="12" lg="6">
                    <h4 class="text-button text-primary">Estado</h4>
                    <v-divider class="mb-2" />
                    <v-skeleton-loader v-if="loading" type="button" />
                    <v-switch
                      v-else
                      v-model="data.state"
                      color="success"
                      inset
                      true-value="ACTIVE"
                      false-value="INACTIVE"
                      :loading="saving"
                      :disabled="saving"
                      :label="data.state === 'ACTIVE' ? 'Activo' : 'Inactivo'"
                      hint="Un certificado inactivo no puede usarse para firmar."
                      persistent-hint
                      @update:model-value="updateState"
                    />
                  </v-col>
                </v-row>
              </v-container>

              <v-card-actions>
                <v-btn
                  variant="text"
                  color="indigo"
                  :disabled="loading || !data.pem"
                  @click="downloadPem"
                >
                  <v-icon class="mr-1">mdi-download</v-icon>Descargar PEM
                </v-btn>
                <v-spacer />
                <v-btn variant="text" @click="router.push('/my/certificates')">Volver</v-btn>
              </v-card-actions>
            </v-tabs-window-item>

            <!-- ── permisos ─────────────────────────────────────────────── -->
            <v-tabs-window-item value="perms">
              <ag-permission-table v-if="!loading" :fingerprint="fingerprint" />
            </v-tabs-window-item>
          </v-tabs-window>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { saveAs } from "file-saver";
import type { CertificateState, Mode2FA } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useNotificationsStore } from "@/stores/notifications";
import { useAuthStore } from "@/stores/auth";
import { mode2FALabel } from "@/utils/labels";
import AgPermissionTable from "@/components/AgPermissionTable.vue";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const { report } = useApiErrors();
const notifications = useNotificationsStore();

const fingerprint = computed(() => String(route.params.fingerprint));
const isAdmin = computed(() => auth.hasAnyAdmin);

const tab = ref("cert");
const loading = ref(true);
const saving = ref(false);
const data = ref({
  fingerprint: "",
  serialnumber: "",
  state: "ACTIVE" as CertificateState,
  key_id: undefined as number | undefined,
  csr_id: undefined as number | undefined,
  pem: "",
  valid_after: undefined as string | undefined,
  valid_before: undefined as string | undefined,
  twofa_mode: "NONE" as Mode2FA,
});

function formatDate(value?: string) {
  return value ? new Date(value).toLocaleString("es-CL") : "—";
}

const expiryHint = computed(() => {
  if (!data.value.valid_before) return undefined;
  const days = Math.floor(
    (new Date(data.value.valid_before).getTime() - Date.now()) / 86_400_000
  );
  if (days < 0) return `Venció hace ${Math.abs(days)} día(s).`;
  if (days <= 30) return `Vence en ${days} día(s).`;
  return undefined;
});

async function load() {
  try {
    Object.assign(data.value, await banco.user.certificates.get(fingerprint.value));
  } catch (err) {
    report(err, {
      title: "Certificado",
      fallback: "Ocurrió un error al recuperar el certificado.",
    });
  } finally {
    loading.value = false;
  }
}

async function updateState(value: CertificateState | null) {
  if (!value) return;
  saving.value = true;
  try {
    await banco.user.certificates.update(fingerprint.value, { state: value });
    notifications.success(
      value === "ACTIVE" ? "Certificado activado." : "Certificado desactivado.",
      "Guardado"
    );
  } catch (err) {
    data.value.state = value === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    report(err, {
      title: "Certificado",
      fallback: "Ocurrió un error al cambiar el estado del certificado.",
    });
  } finally {
    saving.value = false;
  }
}

function downloadPem() {
  const blob = new Blob([data.value.pem], { type: "application/x-pem-file" });
  saveAs(blob, "certificado.pem");
}

onMounted(load);
</script>
